import { createContext, useState, useEffect, useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import API from '../config/axiosConfig';
import Loader from '../components/Loader';
import { featureFlagService } from '../services/FeatureFlagService';
import {
  acquireMfaToken,
  MfaUnavailableError,
  beginEntraLogin,
  clearEntraIntent,
  clearMoveToken,
  clearNextSignIn,
  consumeEntraIntent,
  getMoveToken,
  setMoveError,
  signInIdp,
  getAuthRuntime,
  hasEntraAccount,
  hasEntraSignInError,
  rejectEntraSession,
  forgetEntraAccounts,
  logoutEntra,
  peekEntraIntent,
} from '../auth/runtimeAuth';
import { isSessionInvalidError, setSessionInvalidHandler } from '../auth/sessionInvalid';
import { markMigrated } from '../auth/migratedFlag';

export const UserContext = createContext();

export const UserProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [hospitals, setHospitals] = useState([]);
  const [permittedApps, setPermittedApps] = useState([]);
  const [pendingRequests, setPendingRequests] = useState([]);
  const [userConfig, setUserConfig] = useState(null);
  const [isInitialized, setIsInitialized] = useState(false);
  const [legacyToken, setLegacyToken] = useState(localStorage.getItem('token'));
  const [entraSession, setEntraSession] = useState(hasEntraAccount());
  const [entraSessionRejected, setEntraSessionRejected] = useState(false);
  const [lastAuthResult, setLastAuthResult] = useState(null);
  const [authError, setAuthError] = useState(getAuthRuntime().error || '');
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [isLeavingForSignOut, setIsLeavingForSignOut] = useState(false);
  const entraBootstrapRef = useRef(null);
  const restoringMoveRef = useRef(false);
  // The intent this page's Microsoft sign-in came back with, for "sign in with
  // another account" after it is refused (signInWithAnotherAccount).
  const signInIntentRef = useRef(null);
  const hasRestoredEntraSession = !entraSessionRejected && (entraSession || hasEntraAccount());
  const authMethod = legacyToken ? 'legacy' : hasRestoredEntraSession ? 'entra' : null;
  // Kept as a truthy compatibility value for existing route/app checks. Entra
  // access tokens remain inside MSAL and are never copied into this value.
  const authToken = legacyToken || (hasRestoredEntraSession ? 'entra' : null);
  const authCapabilities = getAuthRuntime().capabilities;

  // A move whose link was refused (another email, a super admin, …) goes back to
  // the legacy session it came from, with the reason, instead of ending signed
  // out (D21). A fresh page load, so nothing of the Microsoft attempt is left.
  const restoreLegacySessionAfterMove = (moveToken, error) => {
    restoringMoveRef.current = true;
    clearMoveToken();
    setMoveError(error?.response?.data?.error?.hebMessage || 'המעבר לכניסה החדשה לא הושלם. נסו שוב.');
    localStorage.setItem('token', moveToken);
    rejectEntraSession()
      .catch(rejectError => console.error('Clearing the Microsoft session failed:', rejectError))
      .finally(() => window.location.replace('/profile'));
  };

  // keepSessionOnError: on the initial load a failed /me has nothing to fall back to,
  // so the session is cleared and the user is bounced to login. A *refresh* of an
  // already-live session (onboarding, after a role change) must not do that — a
  // transient failure there would log the user out mid-flow, when the previous,
  // slightly stale snapshot is a perfectly good fallback.
  const fetchUserData = useCallback(async ({ keepSessionOnError = false } = {}) => {
    if (!authToken) return false;

    try {
      if (authMethod === 'entra' && !entraBootstrapRef.current) {
        const intent = consumeEntraIntent();
        signInIntentRef.current = intent;
        // A move sends its legacy token along, so the server knows which account
        // is moving (docs/EntraFullMigrationPlan.md §3.1).
        const moveToken = intent.intent === 'migrate' ? getMoveToken() : null;
        // A label for the sign-in method, which only the ID token carries.
        const idp = signInIdp();
        const request = intent.invitationToken
          ? API.post('/api/auth/entra/register-from-invitation', { token: intent.invitationToken, idp })
          : API.post('/api/auth/entra/bootstrap', { moveToken, idp }, { handlingErrorLocally: Boolean(moveToken) });
        if (moveToken) {
          request.then(clearMoveToken, (error) => restoreLegacySessionAfterMove(moveToken, error));
        }
        entraBootstrapRef.current = request.then(response => ({
          ...response.data,
          intent: intent.invitationToken ? 'invitation' : intent.intent,
          returnTo: intent.returnTo || null,
        }));
      }
      if (authMethod === 'entra') {
        const bootstrapResult = await entraBootstrapRef.current;
        setLastAuthResult(bootstrapResult);
        // From now on /login sends this browser straight to Microsoft (auth/migratedFlag).
        markMigrated();
      }
      let response = await API.get('/api/users/me');
      // Two-step verification is required and this token has not proved it:
      // get one that has before loading anything else (see MfaPolicy).
      if (authMethod === 'entra' && response.data.mfa?.required && !response.data.mfa.satisfied) {
        try {
          const token = await acquireMfaToken();
          if (!token) return false; // leaving for Microsoft's code page
          response = await API.get('/api/users/me');
        } catch (mfaError) {
          if (!(mfaError instanceof MfaUnavailableError)) throw mfaError;
          // Continue without it: the server keeps treating the user as unverified.
          window.showFloatingError?.({ hebMessage: 'לא ניתן להשלים אימות דו-שלבי. פנו למנהל המערכת.' });
        }
      }
      const userData = response.data;
      setUser(userData);
      setAuthError('');
      return userData;
    } catch (error) {
      // The page is reloading into the legacy session (restoreLegacySessionAfterMove).
      if (restoringMoveRef.current) return false;
      console.error('Failed to fetch user data:', error);
      const message = error.serverError?.hebMessage
        || error.response?.data?.error?.hebMessage
        || 'ההתחברות נכשלה';
      if (!keepSessionOnError) {
        // A dead session (see auth/sessionInvalid) goes to the login page with no message.
        if (!isSessionInvalidError(error)) setAuthError(message);
        if (authMethod === 'legacy') {
          localStorage.removeItem('token');
          setLegacyToken(null);
        } else {
          setEntraSessionRejected(true);
          setEntraSession(false);
          rejectEntraSession().catch(rejectError => console.error('Clearing the Microsoft session failed:', rejectError));
        }
        setUser(null);
      }
      return false;
    }
  }, [authMethod, authToken]);

  const fetchHospitals = useCallback(async () => {
    try {
      const response = await API.get('/api/hospitals');
      setHospitals(response.data);
      return true;
    } catch (error) {
      console.error('Fetching hospitals failed:', error);
      return false;
    }
  }, []);

  const fetchPermittedApps = useCallback(async () => {
    try {
      const response = await API.get('/api/apps/permitted');
      setPermittedApps(response.data);
      return true;
    } catch (error) {
      console.error('Fetching permitted apps failed:', error);
      return false;
    }
  }, []);

  const fetchUserConfig = useCallback(async () => {
    try {
      const response = await API.get('/api/user-config');
      setUserConfig(response.data);
      return true;
    } catch (error) {
      console.error('Fetching user config failed:', error);
      return false;
    }
  }, []);

  const fetchPendingRequests = useCallback(async () => {
    try {
      const response = await API.get('/api/users/pending-requests');
      setPendingRequests(response.data);
      return true;
    } catch (error) {
      console.error('Fetching pending requests failed:', error);
      return false;
    }
  }, []);

  const [outdatedProtocols, setOutdatedProtocols] = useState([]);

  const fetchOutdatedProtocols = useCallback(async () => {
    try {
      const response = await API.get('/api/protocols/outdated');
      setOutdatedProtocols(response.data);
      return true;
    } catch (error) {
      console.error('Fetching outdated protocols failed:', error);
      return false;
    }
  }, []);

  // Everything that depends on *who* the user is and what they belong to.
  // Kept separate from initializeData (which owns the loader / isInitialLoad
  // bookkeeping) so that flows which change the user's roles while the token stays
  // the same — onboarding creating or joining a department, for instance — can pull
  // the whole context forward without a page reload.
  const loadUserScopedData = useCallback(async ({ keepSessionOnError = false } = {}) => {
    const userData = await fetchUserData({ keepSessionOnError });
    if (!userData) {
      return false;
    }

    // Incomplete Entra accounts only need their self-service onboarding data. A
    // pending join request is past onboarding and loads like any roleless user.
    if (
      authMethod === 'entra'
      && !['completed', 'pending_approval'].includes(userData.entraOnboarding?.status)
    ) {
      return true;
    }

    const [hospitalsFetched, appsFetched] = await Promise.all([
      fetchHospitals(),
      fetchPermittedApps(),
      fetchUserConfig()
    ]);

    if (!hospitalsFetched || !appsFetched) {
      return false;
    }

    const promises = [];
    // Fetch pending requests if user is admin
    if (userData.isSuperAdmin || userData.roles?.some(role => role.role === 'ADMIN')) {
      promises.push(fetchPendingRequests());
    }
    // Fetch outdated protocols if user is editor or admin or superadmin
    if (userData.isSuperAdmin || userData.roles?.some(role => role.role === 'EDITOR' || role.role === 'ADMIN')) {
      promises.push(fetchOutdatedProtocols());
    }
    await Promise.all(promises);
    return true;
  }, [authMethod, fetchUserData, fetchHospitals, fetchPermittedApps, fetchUserConfig, fetchPendingRequests, fetchOutdatedProtocols]);

  const initializeData = useCallback(async () => {
    if (!authToken) {
      setLoading(false);
      setIsInitialized(false);
      setUser(null);
      setHospitals([]);
      // isInitialLoad is left alone: `loading` already ungates rendering, and keeping
      // the flag set means a later login in this session still gets the full-page
      // loader instead of the route-level one.
      return;
    }

    // Only show full-page loader on initial load, not when switching tokens
    if (isInitialLoad) {
      setLoading(true);
    }

    try {
      if (await loadUserScopedData()) {
        setIsInitialized(true);
      }
    } catch (error) {
      console.error('Initialization failed:', error);
      localStorage.removeItem('token');
      setLegacyToken(null);
      setEntraSession(false);
    } finally {
      setLoading(false);
      setIsInitialLoad(false);
    }
  }, [authToken, loadUserScopedData, isInitialLoad]);


  useEffect(() => {
    initializeData();
  }, [initializeData]);

  // A password or legacy Google sign-in. The newest sign-in wins: leaving for
  // Microsoft drops the legacy token (beginEntraLogin), and this drops any
  // Microsoft account left in the browser.
  const login = async (token) => {
    forgetEntraAccounts().catch(error => console.error('Clearing the Microsoft account failed:', error));
    localStorage.setItem('token', token);
    setLegacyToken(token);
    setEntraSessionRejected(false);
    setEntraSession(false);
    setAuthError('');
  };

  // Back to signed out, without telling Microsoft. Shared by logout and by the
  // quiet sign-out of a session the server no longer accepts.
  const resetSession = useCallback(() => {
    localStorage.removeItem('token');
    setLegacyToken(null);
    setEntraSessionRejected(false);
    setEntraSession(false);
    entraBootstrapRef.current = null;
    setLastAuthResult(null);
    setAuthError('');
    setUser(null);
    setHospitals([]);
    setPermittedApps([]);
    setPendingRequests([]);
    setOutdatedProtocols([]);
    setUserConfig(null);
    setIsInitialized(false);
    // Back to a cold start, so the next login gets the full-page loader rather than
    // the route-level one.
    setIsInitialLoad(true);
  }, []);

  // A session that ended because the account moved to Microsoft sign-in goes
  // straight to Microsoft's page (/signin), not to the legacy login.
  useEffect(() => setSessionInvalidHandler((reason) => {
    resetSession();
    if (reason === 'migrated') window.location.replace('/signin');
  }), [resetSession]);

  const logout = async ({ nextSignIn = null } = {}) => {
    // A legacy session signs out locally: a Microsoft account left in the browser
    // is only dropped, never a reason to go through Microsoft's sign-out.
    const shouldLogoutEntra = authMethod === 'entra'
      || (authMethod !== 'legacy' && (hasEntraAccount() || hasEntraSignInError()));
    clearEntraIntent();
    if (shouldLogoutEntra) {
      // Leaving for Microsoft's sign-out, which comes back to /signin on a fresh
      // page. Clearing the session first would drop to the signed-out routes and
      // show /login for a moment before the redirect, so the page stays under a
      // loader instead.
      setIsLeavingForSignOut(true);
      localStorage.removeItem('token');
      try {
        await logoutEntra({ nextSignIn });
        return;
      } catch (error) {
        console.error('Microsoft sign-out failed; signing out locally:', error);
        clearNextSignIn();
        setIsLeavingForSignOut(false);
      }
    }
    if (authMethod === 'legacy' && hasEntraAccount()) {
      await forgetEntraAccounts().catch(error => console.error('Clearing the Microsoft account failed:', error));
    }
    resetSession();
  };

  // After a refused Microsoft sign-in: sign out of Microsoft, and on /signin try
  // the same thing again with another account, so a refused invitation is
  // completed rather than replaced by a plain sign-in (a new account without the
  // invited department), and the page the user was going to is kept.
  const signInWithAnotherAccount = () => {
    const failed = signInIntentRef.current || peekEntraIntent();
    return logout({
      nextSignIn: failed && { invitationToken: failed.invitationToken, returnTo: failed.returnTo },
    });
  };

  // The redirect reloads the page, so the rejected-session state resets on its
  // own. Clearing it here would revive a session that just failed, and if the
  // redirect cannot start, /signin and the failed load would retry each other
  // forever.
  const loginWithEntra = async (options = {}) => {
    setAuthError('');
    await beginEntraLogin(options);
  };

  const updateUserConfig = async (config) => {
    try {
      const response = await API.put('/api/user-config', config);
      setUserConfig(response.data);
      return response.data;
    } catch (error) {
      console.error('Updating user config failed:', error);
      throw error;
    }
  };

  const refreshUserData = () => fetchUserData();

  // Re-read the user *and* everything derived from their roles (hospitals, permitted
  // apps, user config, pending requests, outdated protocols). Call this after an
  // action that changes the user's membership under an already-issued token.
  // Unlike the initial load, a failure here keeps the session: the caller is
  // mid-flow and the existing snapshot is a better fallback than a sign-out.
  const refreshAllUserData = () => loadUserScopedData({ keepSessionOnError: true });

  const createDepartment = async (hospitalId, newDepartment) => {
    try {
      // Callers surface failures (e.g. a duplicate department name) next to the name fields.
      const response = await API.post(
        `/api/hospitals/${hospitalId}/departments`,
        newDepartment,
        { handlingErrorLocally: true }
      );
      fetchHospitals();
      return response.data;
    } catch (error) {
      console.error('Creating department failed:', error);
      throw error;
    }
  };

  // Feature flag checking functions
  const isFeatureEnabled = useCallback((featureName) => {
    return featureFlagService.isFeatureEnabled(
      featureName,
      hospitals,
      user?.isSuperAdmin || false
    );
  }, [hospitals, user]);

  const isFeatureEnabledForHospital = useCallback((featureName, hospital) => {
    return featureFlagService.isFeatureEnabledForHospital(
      featureName,
      hospital,
      user?.isSuperAdmin || false
    );
  }, [user]);

  const isFeatureEnabledForDepartment = useCallback((featureName, hospital, department) => {
    return featureFlagService.isFeatureEnabledForDepartment(
      featureName,
      hospital,
      department,
      user?.isSuperAdmin || false
    );
  }, [user]);

  if ((loading && isInitialLoad) || isLeavingForSignOut) {
    return <Loader isLoading={true} />;
  }

  return (
    <UserContext.Provider
      value={{
        user,
        login,
        logout,
        refreshUserData,
        refreshSessionData: initializeData,
        refreshAllUserData,
        hospitals,
        permittedApps,
        pendingRequests,
        outdatedProtocols,
        userConfig,
        updateUserConfig,
        createDepartment,
        fetchHospitals,
        fetchPermittedApps,
        fetchPendingRequests,
        fetchOutdatedProtocols,
        isInitialized,
        authToken,
        authMethod,
        authCapabilities,
        authError,
        lastAuthResult,
        loginWithEntra,
        signInWithAnotherAccount,
        isFeatureEnabled,
        isFeatureEnabledForHospital,
        isFeatureEnabledForDepartment,
      }}
    >
      {children}
    </UserContext.Provider>
  );
};

UserProvider.propTypes = {
  children: PropTypes.node.isRequired,
};
